import { useMemo } from "react";
import { useInferenceCreateContext } from "domains/inference/contexts/InferenceProvider";
import {
  controlNetComposablePresets,
  controlNetNonComposablePresets,
  ControlNetPreset,
  getAllowedControlNetPreset,
} from "domains/inference/enum/ControlNetPreset";
import ControlSwitch from "domains/ui/components/ControlSwitch";
import Select from "domains/ui/components/Select";
import WithLabelAndTooltip from "domains/ui/components/WithLabelAndTooltip";

export interface SidebarSelectControlNetProps {}

export default function SidebarSelectControlNet({}: SidebarSelectControlNetProps) {
  const {
    referenceControlNet,
    setReferenceControlNet,
    isReferenceModeMappingEnabled,
    setIsReferenceModeMappingEnabled,
    model,
    inferenceType,
    isTexture,
  } = useInferenceCreateContext();

  const { composablePresets, nonComposablePresets } = useMemo(() => {
    const allowedPresets = getAllowedControlNetPreset(model);
    return {
      composablePresets: controlNetComposablePresets.filter((preset) =>
        allowedPresets.composable.includes(preset.value)
      ),
      nonComposablePresets: controlNetNonComposablePresets.filter((preset) =>
        allowedPresets.nonComposable.includes(preset.value)
      ),
    };
  }, [model]);

  const guide = useMemo(() => {
    switch (referenceControlNet) {
      case ControlNetPreset.CANNY:
        return "inference-parameter-controlnet-structure";
      case ControlNetPreset.POSE:
        return "inference-parameter-controlnet-pose";
      case ControlNetPreset.DEPTH:
        return "inference-parameter-controlnet-depth";
      case ControlNetPreset.SEG:
        return "inference-parameter-controlnet-segmentation";
      case ControlNetPreset.ILLUSION:
        return "inference-parameter-controlnet-illusion";
      case ControlNetPreset.CHARACTER:
        return "inference-parameter-controlnet-character";
      case ControlNetPreset.LANDSCAPE:
        return "inference-parameter-controlnet-landscape";
      default:
        return undefined;
    }
  }, [referenceControlNet]);

  const isNonComposable = useMemo(() => {
    return (
      controlNetNonComposablePresets.findIndex(
        (preset) => preset.value === referenceControlNet
      ) !== -1
    );
  }, [referenceControlNet]);

  if (!inferenceType.startsWith("controlnet")) {
    return null;
  }

  return (
    <>
      {!isTexture && (
        <WithLabelAndTooltip label={"ControlNet"} guide={guide}>
          <Select
            w="fit-content"
            mr={-1}
            ml="auto"
            onChange={(e) =>
              setReferenceControlNet(e.target.value as ControlNetPreset)
            }
            value={referenceControlNet ?? ""}
          >
            {composablePresets.map((preset) => (
              <option
                key={preset.value}
                value={preset.value}
                label={`${preset.label}`}
              />
            ))}
            <option disabled>──────────</option>
            {nonComposablePresets.map((preset) => (
              <option
                key={preset.value}
                value={preset.value}
                label={`${preset.label}`}
              />
            ))}
          </Select>
        </WithLabelAndTooltip>
      )}

      {!isNonComposable && (
        <ControlSwitch
          title="Map Detection"
          tooltip="Mode maps visualize the features extracted by and used within the ControlNet"
          isChecked={isReferenceModeMappingEnabled}
          setIsChecked={setIsReferenceModeMappingEnabled}
        />
      )}
    </>
  );
}
